import type { DesiredTrendlineDirection } from '../../../work-periods/comparison-view/helpers/comparison-table.type';
import type { MeasureDetail } from './measure-comparison-artifact.type';
import { ChartType } from './measure-comparison-artifact.type';

const TREND_ICON_MAP: Record<string, string> = {
  up: 'trending_up',
  down: 'trending_down',
  flat: 'trending_flat',
};

/**
 * Checks whether an artifact data object has the grouped measure-comparison shape.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload is grouped by board and exposes groups.
 */
function isGroupedComparisonPayload(data: Record<string, unknown>): boolean {
  return data.grouped_by === 'board' && Array.isArray(data.groups);
}

/**
 * Checks whether an artifact data object has the multi-object comparison shape.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload contains multi-object labels and objects.
 */
function isMultiObjectPayload(data: Record<string, unknown>): boolean {
  return data.mode === 'multi_object' && Array.isArray(data.objects) && Array.isArray(data.labels);
}

/**
 * Checks whether an artifact data object has the single-object measure-comparison shape.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload exposes labels, datasets, and scope.
 */
function isMeasureComparisonPayload(data: Record<string, unknown>): boolean {
  return Array.isArray(data.labels) && Array.isArray(data.datasets) && typeof data.scope === 'string';
}

/**
 * Gets the user-facing label for a measure key.
 *
 * @param {string} measureKey - Backend measure key.
 * @param {MeasureDetail[] | undefined} details - Optional measure metadata.
 * @returns {string} Measure title from metadata or a title-cased key.
 */
function getMeasureLabel(measureKey: string, details?: MeasureDetail[]): string {
  const detail = details?.find((d) => d.name === measureKey);
  return detail?.title ?? measureKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Gets the unit label stored in measure metadata.
 *
 * @param {string} measureKey - Backend measure key.
 * @param {MeasureDetail[] | undefined} details - Optional measure metadata.
 * @returns {string} Unit label, or an empty string when none is configured.
 */
function getMeasureUnits(measureKey: string, details?: MeasureDetail[]): string {
  const detail = details?.find((d) => d.name === measureKey);
  return detail?.units ?? '';
}

/**
 * Gets the desired trendline direction stored in measure metadata.
 *
 * @param {string} measureKey - Backend measure key.
 * @param {MeasureDetail[] | undefined} details - Optional measure metadata.
 * @returns {DesiredTrendlineDirection | undefined} Desired trend direction, when configured.
 */
function getDesiredDirection(measureKey: string, details?: MeasureDetail[]): DesiredTrendlineDirection | undefined {
  const detail = details?.find((d) => d.name === measureKey);
  return detail?.desired_trendline_direction as DesiredTrendlineDirection | undefined;
}

/**
 * Reads the initial chart type from the export-route `initialState` seed.
 * Returns `null` when the seed is absent so the component can fall back to
 * its config-derived default.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {ChartType | null} The validated seed, or `null` when no override applies.
 */
function getInitialChartType(initialState: Record<string, unknown> | undefined): ChartType | null {
  const value = initialState?.chartType;
  if (value === ChartType.Line || value === ChartType.Bar || value === ChartType.Table) return value;
  return null;
}

/**
 * Reads the initial selected-measure key from the export-route `initialState` seed.
 * Only used by the multi-object chart variant.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {string | null} The seed value when present and string-shaped, else `null`.
 */
function getInitialSelectedMeasure(initialState: Record<string, unknown> | undefined): string | null {
  const value = initialState?.selectedMeasure;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export {
  TREND_ICON_MAP,
  getDesiredDirection,
  getInitialChartType,
  getInitialSelectedMeasure,
  getMeasureLabel,
  getMeasureUnits,
  isGroupedComparisonPayload,
  isMeasureComparisonPayload,
  isMultiObjectPayload,
};
